/* hs-eslint ignored failing-rules */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useState, useCallback, useEffect } from 'react';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import Small from 'UIComponents/elements/Small';
import H2 from 'UIComponents/elements/headings/H2';
import H5 from 'UIComponents/elements/headings/H5';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILink from 'UIComponents/link/UILink';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import AdAccountCreationData from 'ads-account-connection/data/AdAccountCreationData';
import AdwordsAdAccountForm from 'ads-account-connection/components/ad-accounts/AdwordsAdAccountForm';
import AdwordsStepIndicators from 'ads-account-connection/components/ad-accounts/AdwordsStepIndicators';
import { ACCOUNT_SETUP_ORDER, ACCOUNT_SETUP_STEPS } from 'ads-account-connection/lib/AdwordsSetupStatus';
import Networks from 'ads-account-connection/lib/Networks';
import { AD_CREDIT_URLS } from 'ads-account-connection/lib/AdAccountHelpUrls';
import { Metrics } from 'ads-account-connection/lib/Metrics';

const AdwordsAdAccountCreation = ({
  onCancel,
  currentStep,
  onStepChange,
  setInviteLink,
  setAdAccountId,
  networkUserId,
  trackEvent,
  isDeveloperPortal
}) => {
  const [adAccountName, setAdAccountName] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [timeZone, setTimeZone] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    trackEvent('accountCreationAction', {
      action: 'Shown the form for account creation',
      network: Networks.ADWORDS
    });
  }, [trackEvent]);

  const LearnMoreLink = ({
    children,
    href
  }) => /*#__PURE__*/_jsx(UILink, {
    external: true,
    href: href,
    onClick: e => e.stopPropagation(),
    children: children
  });

  const createAdAccount = useCallback(() => {
    setLoading(true);
    const payload = {
      adAccountName,
      email,
      currency: selectedCurrency,
      timeZone,
      networkUserId
    };
    AdAccountCreationData.createAdwordsAdAccount(payload).then(response => {
      trackEvent('accountCreationAction', {
        action: 'Successfully submitted the form for account creation',
        network: Networks.ADWORDS
      });
      setError(false);
      setInviteLink(response.accountInviteLink);
      setAdAccountId(response.adAccountId);
      onStepChange(ACCOUNT_SETUP_STEPS.SETUP_BILLING);
      Metrics.counter('create-ad-account', {
        network: 'ADWORDS'
      }).increment();
    }).catch(err => {
      trackEvent('accountCreationAction', {
        action: 'Submitted the form, but received an error',
        network: Networks.ADWORDS,
        errorType: err.responseJSON && err.responseJSON.message
      });

      if (err.responseJSON) {
        setError(err.responseJSON.message);
      }
    }).finally(() => {
      setLoading(false);
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }, [adAccountName, email, networkUserId, onStepChange, selectedCurrency, setAdAccountId, setInviteLink, timeZone, trackEvent]);

  const onAdAccountNameChange = e => {
    setAdAccountName(e.target.value);
  };

  const onEmailChange = e => {
    setEmail(e.target.value);
  };

  const onCurrencyChange = e => {
    setSelectedCurrency(e.target.value);
  };

  const onTimeZoneChange = e => {
    setTimeZone(e.target.value);
  };

  const emailErrorHandler = () => {
    setIsEmailValid(false);
  };

  const emailSuccessHandler = () => {
    setIsEmailValid(true);
  };

  const isMissingField = !adAccountName || !email || !selectedCurrency || !timeZone;
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onCancel
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.adwordsSetup.header"
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx(AdwordsStepIndicators, {
        stepIndex: ACCOUNT_SETUP_ORDER.indexOf(currentStep)
      }), /*#__PURE__*/_jsx(H5, {
        className: "m-top-4 m-bottom-4 text-center",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.adwordsSetup.createAccountTitle"
        })
      }), !isDeveloperPortal && /*#__PURE__*/_jsx(Small, {
        use: "help",
        style: {
          whiteSpace: 'normal'
        },
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.coupon_jsx",
          options: {
            href: AD_CREDIT_URLS[Networks.ADWORDS]
          },
          elements: {
            Link: LearnMoreLink
          }
        })
      }), /*#__PURE__*/_jsx(AdwordsAdAccountForm, {
        adAccountName: adAccountName,
        email: email,
        currency: selectedCurrency,
        timeZone: timeZone,
        onAdAccountNameChange: onAdAccountNameChange,
        onEmailChange: onEmailChange,
        emailErrorHandler: emailErrorHandler,
        emailSuccessHandler: emailSuccessHandler,
        onTimeZoneChange: onTimeZoneChange,
        onCurrencyChange: onCurrencyChange,
        error: error
      })]
    }), /*#__PURE__*/_jsx(UIDialogFooter, {
      align: "right",
      children: /*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.adwordsSetup.missingFields"
        }),
        use: "default",
        disabled: !isMissingField,
        placement: "bottom left",
        children: /*#__PURE__*/_jsx(UILoadingButton, {
          use: "primary",
          onClick: createAdAccount,
          loading: !!loading,
          failed: !!error,
          disabled: isMissingField || !isEmailValid,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "ADS_ACCOUNT_CONNECTION.adAccountConnectModal.adwordsSetup.createAccount"
          })
        })
      })
    })]
  });
};

export default AdwordsAdAccountCreation;